"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "next/navigation";
import { login } from "@/components/(auth)/login/login.action";
import Logger from "@/components/providers/analytics-provider";
import { DASHBOARD_ROUTES } from "@/constants/routes";
import { emailSchema, passwordSchema } from "./login-form.schemas";
import type {
  Step,
  EmailFormData,
  PasswordFormData,
  LoadingAction,
} from "./login-form.types";
import { EmailStep } from "./email-step";
import { PasswordStep } from "./password-step";

export function LoginForm() {
  const [step, setStep] = useState<Step>("initial");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState<LoadingAction>(null);
  const router = useRouter();

  const emailForm = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: "" },
  });

  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { email: "", password: "" },
  });

  const email = emailForm.watch("email");

  const handlePasswordClick = async () => {
    const isValid = await emailForm.trigger("email");
    if (isValid) {
      passwordForm.setValue("email", email);
      setError("");
      setStep("password");
    }
  };

  const handlePasswordSubmit = async (data: PasswordFormData) => {
    setError("");
    setIsLoading(true);
    setLoadingAction("password");

    try {
      const result = await login(data);

      if (result && "error" in result && result.error) {
        setError(result.error || "Authentication failed");
        setIsLoading(false);
        setLoadingAction(null);
        return;
      }

      if (result && result.success) {
        if ("redirect" in result && result.redirect) {
          router.push(result.redirect);
          return;
        }

        router.push(DASHBOARD_ROUTES.ROOT);
        return;
      }
    } catch (error) {
      setError("An error occurred during sign in");
      Logger.error(
        "Sign in error",
        { email: data.email, action: "password_login" },
        error instanceof Error ? error : undefined
      );
      setIsLoading(false);
      setLoadingAction(null);
    }
  };

  const handleBack = () => {
    setError("");
    setStep("initial");
    passwordForm.setValue("password", "");
  };

  if (step === "initial") {
    return (
      <EmailStep
        form={emailForm}
        isLoading={isLoading}
        error={error}
        onPasswordClick={handlePasswordClick}
      />
    );
  }

  if (step === "password") {
    return (
      <PasswordStep
        form={passwordForm}
        email={email}
        isLoading={isLoading}
        loadingAction={loadingAction}
        error={error}
        onSubmit={handlePasswordSubmit}
        onBack={handleBack}
      />
    );
  }

  return null;
}
